import React, { useState } from 'react';
import { QueryObserverResult } from 'react-query';
import { useSelector } from 'react-redux';
import { Button } from 'src/components/atoms';
import {
  StyledIcon,
  StyledIconInButton,
} from 'src/components/foundations/foundations.styles';
import {
  handleErrorToast,
  handleSuccessToast,
  LinkedCaseDetailsModal,
} from 'src/components/molecules';
import { hiddenDots } from 'src/components/pages/Case/helpers/hiddenDots';
import {
  GetCaseLinksByCaseIdQuery,
  ReportStatus,
  useUnlinkCasesMutation,
} from 'src/graphql';
import { useTheme } from 'styled-components';
import { capitaliseFirstLetter } from 'src/utils';
import {
  trackCaseLinkDetailsViewedEvent,
  trackCaseLinkNavigationClickedEvent,
} from 'src/utils/analytics';
import {
  LinkedCaseAccordion,
  LinkedCaseAccordionTitle,
  LinkedCaseItems,
  LinkedCasesContainer,
  LinkedCaseTitleWrapper,
  LinkMoreCaseButton,
  StyledLinkId,
  StyledLinkType,
} from './CaseLinksPanel.styles';
import { caseLinksPanel } from './CaseLinksPanel.vm';

interface Props {
  openLinkCaseModal: (e: React.MouseEvent) => void;
  refetchCaseLinks: () => Promise<
    QueryObserverResult<GetCaseLinksByCaseIdQuery, unknown>
  >;
}

export const CaseLinksPanel = ({
  openLinkCaseModal,
  refetchCaseLinks,
}: Props) => {
  const theme = useTheme();
  const [selectedLink, setSelectedLink] = useState<
    NonNullable<GetCaseLinksByCaseIdQuery['getCaseLinksByCaseId']>[0] | null
  >(null);
  const {
    caseId,
    caseLinks,
    numberOfLinkedCases,
    permissions: { canEditCaseDetails },
    segmentEnabled,
    organisationId,
    userRole,
  } = useSelector(caseLinksPanel);
  const formatLinkType = (linkType: string) => {
    const formatted = linkType.replaceAll('_', ' ');
    return capitaliseFirstLetter(formatted);
  };

  const { mutate: unlinkCases } = useUnlinkCasesMutation({
    onSuccess: () => {
      setSelectedLink(null);
      handleSuccessToast({
        toastMessage: 'Case unlinking was successful',
        toastDescription: 'Case unlinked successfully.',
      });
      refetchCaseLinks();
    },
    onError: () => {
      setSelectedLink(null);
      handleErrorToast({
        toastMessage: 'Case unlinking failed',
        toastDescription: 'Unable to unlink the case. Please try again.',
      });
    },
  });

  const selectedLinkedCase = selectedLink
    ? selectedLink?.SourceCase?.id === caseId
      ? selectedLink.TargetCase
      : selectedLink.SourceCase
    : null;
  return (
    <>
      <LinkedCasesContainer>
        <LinkedCaseAccordion
          className="linked-cases-accordion"
          topBorderRadius="6px"
          bottomBorderRadius="6px"
          title={
            <LinkedCaseTitleWrapper>
              <StyledIcon
                className="uil uil-link"
                size="18px"
                color={
                  theme.isVaultVariant
                    ? theme.newPalette.neutral.N900
                    : theme.atlas.semantic.color.type.default
                }
              />
              <LinkedCaseAccordionTitle>
                Linked cases
                {numberOfLinkedCases > 0 ? ` (${numberOfLinkedCases})` : ''}
              </LinkedCaseAccordionTitle>
            </LinkedCaseTitleWrapper>
          }
          defaultOpen={false}
          data-testid="linked-cases-accordion"
          backgroundColor={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N100
              : theme.atlas.semantic.color.surface.subtle
          }
          bodyBackgroundColor={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N100
              : theme.atlas.semantic.color.surface.subtle
          }
          hoverColor={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N100
              : theme.atlas.semantic.color.surface.subtle
          }
          topBorder={true}
          bottomBorder={false}
          containerBorderColor={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N300
              : theme.atlas.semantic.color.ui.divider.default
          }
          hideContentBorder
        >
          <LinkedCaseItems>
            {caseLinks.map((link, index) => {
              const linkedCase =
                link.SourceCase?.id === caseId
                  ? link.TargetCase
                  : link.SourceCase;
              return (
                <Button
                  key={link.id}
                  variant="secondary"
                  size="small"
                  data-testid={`linked-case-button-${index}`}
                  onClick={() => {
                    setSelectedLink(link);
                    if (segmentEnabled && organisationId && userRole) {
                      const userCanViewBothCases = Boolean(
                        link.SourceCase?.AssociatedPermissions
                          ?.canViewCaseDetails &&
                        link.TargetCase?.AssociatedPermissions
                          ?.canViewCaseDetails,
                      );
                      trackCaseLinkDetailsViewedEvent({
                        organisation_id: organisationId,
                        source_case_id: link.SourceCase?.id ?? null,
                        target_case_id: link.TargetCase?.id ?? null,
                        current_case_id: caseId,
                        user_role: userRole,
                        link_type: link.linkType,
                        has_note: Boolean(link.notes),
                        case_status_source:
                          (link.SourceCase?.status as ReportStatus) ?? null,
                        case_status_target:
                          (link.TargetCase?.status as ReportStatus) ?? null,
                        is_link_masked: !userCanViewBothCases,
                      });
                    }
                  }}
                >
                  <StyledLinkId>
                    {linkedCase?.AssociatedPermissions?.canViewCaseDetails
                      ? linkedCase.readableId.toUpperCase()
                      : hiddenDots}
                  </StyledLinkId>
                  |
                  <StyledLinkType>
                    {formatLinkType(link.linkType)}
                  </StyledLinkType>
                </Button>
              );
            })}

            {canEditCaseDetails && (
              <LinkMoreCaseButton
                variant="tertiary"
                size="small"
                noTextWrap
                data-testid="case-link-panel-link-more-case-button"
                onClick={openLinkCaseModal}
              >
                <StyledIconInButton
                  size="small"
                  className={'uil uil-plus'}
                  position="left"
                />
                Link case
              </LinkMoreCaseButton>
            )}
          </LinkedCaseItems>
        </LinkedCaseAccordion>
      </LinkedCasesContainer>
      {selectedLink && (
        <LinkedCaseDetailsModal
          data-testid="linked-case-details-modal"
          open={Boolean(selectedLink)}
          setOpen={() => {
            if (selectedLink) {
              setSelectedLink(null);
            }
          }}
          canViewCaseDetails={
            selectedLinkedCase?.AssociatedPermissions?.canViewCaseDetails ??
            false
          }
          canUnlinkCase={
            (selectedLinkedCase?.AssociatedPermissions?.canEditCaseDetails ??
              false) &&
            canEditCaseDetails
          }
          caseId={selectedLinkedCase?.id}
          readableId={selectedLinkedCase?.readableId}
          caseName={selectedLinkedCase?.name}
          caseStatus={selectedLinkedCase?.status as ReportStatus}
          linkDate={selectedLink.createdAt}
          linkType={selectedLink.linkType}
          notes={selectedLink.notes}
          linkedBy={selectedLink.CreatedByUser?.name}
          unlinkCase={() => {
            if (selectedLink) {
              unlinkCases({ linkId: selectedLink.id });
            }
          }}
          onViewLinkedCase={() => {
            if (segmentEnabled && organisationId && userRole) {
              trackCaseLinkNavigationClickedEvent({
                organisation_id: organisationId,
                source_case_id: selectedLink.SourceCase?.id ?? null,
                target_case_id: selectedLink.TargetCase?.id ?? null,
                current_case_id: caseId,
                user_role: userRole,
                link_type: selectedLink.linkType,
                has_note: Boolean(selectedLink.notes),
                case_status_source:
                  (selectedLink.SourceCase?.status as ReportStatus) ?? null,
                case_status_target:
                  (selectedLink.TargetCase?.status as ReportStatus) ?? null,
              });
            }
          }}
        />
      )}
    </>
  );
};
